import { css } from '@emotion/react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Chip, Stack, Switch, Typography } from '@mui/material';
import { BasicDragHandle, SortableItem } from '../../../components/SortableList/SortableItem';
import {
  KpiSourceCatalogEntry,
  KpiSourceId,
  KpiSourceTag,
} from '../../../data-models/kpi-source-hierarchy.data-model';
import { cardBorderStyles } from '../../../theme/component-styles';
import { ColorPalette } from '../../../theme/structure';

// Same chrome as the summary-page panels, so the rows read as cards of that family.
const rowCss = css({
  ...cardBorderStyles,
  display: 'grid',
  gridTemplateColumns: 'min-content 2.5rem 1fr min-content',
  alignItems: 'center',
  columnGap: '0.75rem',
  padding: '0.25rem 0.75rem 0.25rem 0',
  background: 'var(--mui-palette-background-paper)',
});

const lockedRowCss = css(rowCss, {
  gridTemplateColumns: 'min-content 2.5rem 1fr',
  background: 'var(--mui-colors-neutral-5)',
});

// The drag handle's footprint — its IconButton (2.125rem in this theme) plus the wrapper's
// 0.25rem padding — reserved on every row, so the columns line up and a row keeps its height
// whether it shows a handle, a lock, or nothing.
const handleSlotCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.625rem;
  height: 2.625rem;
`;

const lockIconCss = css`
  color: var(--mui-colors-neutral-50);
`;

// Only the content fades on an excluded source: the handle and the switch stay at full
// strength, so the row reads as "left out", not as a disabled control. The transition sits
// on the content in both states so the fade runs both ways.
const fadableCss = css`
  transition: opacity 0.15s ease;
`;

const excludedCss = css`
  opacity: 0.55;
`;

const rankCss = css`
  font-variant-numeric: tabular-nums;
  text-align: center;
`;

// Pale fill, dark text — the darker shades are what keeps 11px legible. Purple
// is the Foresight brand scale; blue (primary) is reserved for AI, as elsewhere.
type TagColors = (colors: ColorPalette) => { backgroundColor: string; color: string };
// neutral[80] is a 12% overlay in this palette, not a dark grey — 70 is the darkest solid.
const NEUTRAL_TAG: TagColors = (c) => ({ backgroundColor: c.neutral[10], color: c.neutral[70] });
const TAG_COLORS: Record<KpiSourceTag, TagColors> = {
  'Foresight Collection': (c) => ({ backgroundColor: c.secondary[10], color: c.secondary[80] }),
  'Document Hub': (c) => ({ backgroundColor: c.warning[10], color: c.warning[60] }),
  'Third party': NEUTRAL_TAG,
  'Manual entry': (c) => ({ backgroundColor: c.success[10], color: c.success[70] }),
  'Managed services': (c) => ({ backgroundColor: c.secondary[20], color: c.secondary[90] }),
  AI: (c) => ({ backgroundColor: c.primary[10], color: c.primary[70] }),
};

function SourceTag({ tag }: { tag: KpiSourceTag }) {
  // A tag the API adds later renders neutral rather than breaking the row.
  const colors = TAG_COLORS[tag] ?? NEUTRAL_TAG;
  return (
    <Chip
      label={tag}
      size='small'
      icon={tag === 'AI' ? <AutoAwesomeIcon /> : undefined}
      sx={(theme) => ({
        height: '1.25rem',
        fontSize: '0.6875rem',
        fontWeight: 500,
        ...colors(theme.colors),
        '& .MuiChip-icon': { fontSize: '0.75rem', color: 'inherit', marginLeft: '0.375rem' },
      })}
    />
  );
}

function SourceSummary({
  entry,
  note,
  excluded = false,
}: {
  entry: KpiSourceCatalogEntry;
  note?: string;
  excluded?: boolean;
}) {
  return (
    <Stack minWidth={0} css={[fadableCss, excluded && excludedCss]}>
      <Stack direction='row' alignItems='baseline' columnGap='0.5rem' flexWrap='wrap'>
        <Typography variant='body2' fontWeight={500}>
          {entry.label}
        </Typography>
        {entry.tags.map((tag) => (
          <SourceTag key={tag} tag={tag} />
        ))}
      </Stack>
      {(entry.description || note) && (
        <Typography variant='caption' color='text.secondary'>
          {[entry.description, note].filter(Boolean).join(' ')}
        </Typography>
      )}
    </Stack>
  );
}

interface KpiSourceRowProps {
  entry: KpiSourceCatalogEntry;
  /** Null when the strategy has no order — the cell stays blank rather than showing a meaningless number. */
  rank: number | null;
  enabled: boolean;
  onToggle: (id: KpiSourceId, enabled: boolean) => void;
  /** False renders a static row: no drag handle, and not a sortable item at all. */
  orderable: boolean;
}

export function KpiSourceRow({ entry, rank, enabled, onToggle, orderable }: KpiSourceRowProps) {
  const row = (
    <div css={rowCss} data-testid={`kpi-source-row-${entry.id}`}>
      {orderable ? <BasicDragHandle /> : <div css={handleSlotCss} />}
      <Typography variant='body2' color='text.secondary' css={[rankCss, fadableCss, !enabled && excludedCss]}>
        {rank === null ? '' : enabled ? rank : '—'}
      </Typography>
      <SourceSummary entry={entry} excluded={!enabled} />
      <Switch
        size='small'
        checked={enabled}
        onChange={(_, checked) => onToggle(entry.id, checked)}
        inputProps={{ 'aria-label': `Include ${entry.label}` }}
      />
    </div>
  );
  return orderable ? <SortableItem id={entry.id}>{row}</SortableItem> : row;
}

interface LockedKpiSourceRowProps {
  entry: KpiSourceCatalogEntry;
  /** Null when the strategy has no order. */
  rank: number | null;
  /** Appended to the description: why this row cannot move. */
  note?: string;
}

export function LockedKpiSourceRow({ entry, rank, note }: LockedKpiSourceRowProps) {
  return (
    <div css={lockedRowCss} data-testid={`kpi-source-row-${entry.id}`}>
      <div css={handleSlotCss}>
        <LockOutlinedIcon fontSize='small' css={lockIconCss} titleAccess='Locked' />
      </div>
      <Typography variant='body2' color='text.secondary' css={rankCss}>
        {rank ?? ''}
      </Typography>
      <SourceSummary entry={entry} note={note} />
    </div>
  );
}
